// src/components/DomainCard.js — Settings > Custom Domain (multi-tenant mode only)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getDomain, saveDomain, verifyDomain } from '../utils/api';

const STATUS_COLOR = {
  none:     '#9ca3af',
  pending:  '#d97706',
  verified: '#2563eb',
  active:   '#16a34a',
  failed:   '#dc2626',
};
const STATUS_LABEL = {
  none:     'Not set',
  pending:  'Pending — add the records below',
  verified: 'Verified — provisioning SSL...',
  active:   'Active',
  failed:   'Failed',
};

function CopyField({ label, value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, color: 'var(--gray-400)', marginBottom: 3 }}>{label}</div>
      <div style={{ fontFamily: 'monospace', fontSize: 12, background: 'var(--gray-50)', padding: '8px 12px', borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ wordBreak: 'break-all' }}>{value}</span>
        <button className="btn btn-outline btn-sm" onClick={copy} style={{ flexShrink: 0, padding: '3px 8px' }}>
          <i className={`ti ${copied ? 'ti-check' : 'ti-copy'}`} />
        </button>
      </div>
    </div>
  );
}

export default function DomainCard({ toast }) {
  const [info, setInfo] = useState(null);       // null = loading or feature not enabled
  const [checked, setChecked] = useState(false); // has the initial fetch resolved (success or 404)?
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [checkHint, setCheckHint] = useState('');
  const pollRef = useRef(null);

  const load = useCallback(() => {
    getDomain()
      .then(r => setInfo(r.data))
      .catch(() => setInfo(null))
      .finally(() => setChecked(true));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll while a provisioning run is pending, so "active" shows up without a
  // manual refresh (deploy/provision_domains.php runs on its own timer).
  useEffect(() => {
    clearInterval(pollRef.current);
    if (info?.custom_domain_status === 'verified') {
      pollRef.current = setInterval(load, 5000);
    }
    return () => clearInterval(pollRef.current);
  }, [info?.custom_domain_status, load]);

  if (!checked) return null;      // still loading — avoid a flash of nothing/wrong state
  if (!info) return null;         // GET /api/domain 404'd — MULTI_TENANT isn't enabled here

  const handleSave = async () => {
    if (!input.trim()) return;
    setSaving(true);
    try {
      const r = await saveDomain(input.trim());
      setInfo(r.data);
      setInput('');
      toast('Domain saved — add the DNS records below, then click Verify.');
    } catch (e) {
      toast(e.response?.data?.error || 'Failed to save domain');
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    try {
      const r = await verifyDomain();
      if (r.data.success) {
        setCheckHint('');
        toast('Verified! Provisioning SSL — this can take a few minutes.');
        load();
      } else {
        // Show what DNS actually looks like, so the user can fix the specific
        // record instead of guessing why "not verified" keeps happening.
        setCheckHint(r.data.hint || '');
        const missing = [];
        if (!r.data.cname_ok) missing.push('CNAME/routing');
        if (!r.data.txt_ok) missing.push('TXT');
        toast(`Not verified yet — ${missing.join(' and ')} check failed.`);
      }
    } catch (e) {
      toast(e.response?.data?.error || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const status = info.custom_domain_status || 'none';

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="settings-section-title">Custom Domain</div>

      <div className="form-group" style={{ maxWidth: 400 }}>
        <label className="form-label">Default address</label>
        <div style={{ fontFamily: 'monospace', fontSize: 13, color: 'var(--gray-600)' }}>https://{info.default_domain}</div>
      </div>

      <div className="form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">Custom domain</label>
          <input
            className="form-input"
            value={input || info.custom_domain || ''}
            onChange={e => setInput(e.target.value)}
            placeholder="crm.yourcompany.com"
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 2 }}>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || !input.trim()}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {info.custom_domain && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span
              className="status-badge"
              style={{ background: STATUS_COLOR[status] + '22', color: STATUS_COLOR[status] }}
            >
              {STATUS_LABEL[status] || status}
            </span>
            {status !== 'active' && (
              <button className="btn btn-outline btn-sm" onClick={handleVerify} disabled={verifying}>
                {verifying ? 'Checking...' : 'Verify'}
              </button>
            )}
          </div>

          {checkHint && (
            <div style={{ fontSize: 12, color: 'var(--gray-600)', background: 'var(--amber-light)', border: '0.5px solid #fde68a', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
              <strong style={{ color: '#92400e' }}>What we see right now:</strong> {checkHint}
            </div>
          )}

          {info.last_provision_error && (
            <p style={{ fontSize: 12, color: 'var(--red)', marginBottom: 12 }}>
              Last attempt failed: {info.last_provision_error}
            </p>
          )}

          {status !== 'active' && info.records && (
            <div style={{ marginTop: 4 }}>
              <div style={{ fontSize: 12, color: 'var(--gray-600)', marginBottom: 8 }}>
                Add these records at your DNS provider, then click Verify:
              </div>
              <CopyField label={`CNAME — ${info.records.cname.name}`} value={info.records.cname.value} />
              <CopyField label={`TXT — ${info.records.txt.name}`} value={info.records.txt.value} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
